import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Plus, Edit, Trash2, Mail, ArrowLeft, X, RefreshCw, Send } from 'lucide-react';
import api from '../services/api';

function AdminUsuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [formData, setFormData] = useState({ nombre: '', email: '', rol: 'empresa', empresa_id: '' });
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      const [usuariosRes, empresasRes] = await Promise.all([
        api.get('/admin/usuarios'),
        api.get('/admin/empresas')
      ]);
      setUsuarios(usuariosRes.data.data);
      setEmpresas(empresasRes.data.data.filter(e => e.activo));
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const abrirModal = (usuarioEditar = null) => {
    if (usuarioEditar) {
      setEditando(usuarioEditar);
      setFormData({
        nombre: usuarioEditar.nombre || '',
        email: usuarioEditar.email || '',
        rol: usuarioEditar.rol || 'empresa',
        empresa_id: usuarioEditar.empresa_id || ''
      });
    } else {
      setEditando(null);
      setFormData({ nombre: '', email: '', rol: 'empresa', empresa_id: '' });
    }
    setError('');
    setShowModal(true);
  };

  const guardar = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.rol === 'empresa' && !formData.empresa_id) {
      setError('Selecciona una empresa para usuarios de tipo empresa');
      return;
    }

    try {
      const datos = {
        ...formData,
        empresa_id: formData.rol === 'admin' ? null : formData.empresa_id || null
      };

      if (editando) {
        await api.put(`/admin/usuarios/${editando.id}`, datos);
      } else {
        await api.post('/admin/usuarios', datos);
      }
      setShowModal(false);
      cargarDatos();
    } catch (error) {
      setError(error.response?.data?.error || 'Error guardando usuario');
    }
  };

  const eliminar = async (usuarioEliminar) => {
    if (!window.confirm(`¿Eliminar usuario "${usuarioEliminar.nombre}"?`)) return;

    try {
      await api.delete(`/admin/usuarios/${usuarioEliminar.id}`);
      cargarDatos();
    } catch (error) {
      alert(error.response?.data?.error || 'Error eliminando usuario');
    }
  };

  const toggleActivo = async (usuarioToggle) => {
    try {
      await api.put(`/admin/usuarios/${usuarioToggle.id}`, { activo: !usuarioToggle.activo });
      cargarDatos();
    } catch (error) {
      alert('Error actualizando usuario');
    }
  };

  const enviarResetPassword = async (usuarioReset) => {
    if (!window.confirm(`¿Enviar email de recuperación a ${usuarioReset.email}?`)) return;

    try {
      await api.post(`/admin/usuarios/${usuarioReset.id}/reset-password`, {});
      alert('Email de recuperación enviado');
    } catch (error) {
      alert('Error enviando email');
    }
  };

  const reenviarInvitacion = async (usuarioInvitar) => {
    try {
      await api.post(`/admin/usuarios/${usuarioInvitar.id}/resend-invitation`, {});
      alert('Invitación reenviada');
    } catch (error) {
      alert('Error reenviando invitación');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin/dashboard')} className="text-gray-600 hover:text-gray-900">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Gestión de Usuarios</h1>
              <p className="text-sm text-gray-600">{usuarios.length} usuarios registrados</p>
            </div>
          </div>
          <button
            onClick={() => abrirModal()}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-5 h-5" />
            Nuevo Usuario
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuario</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rol</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Empresa</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Estado</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {usuarios.map((usuario) => (
                  <tr key={usuario.id} className={!usuario.activo ? 'bg-gray-50' : ''}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                          <span className="text-purple-600 font-medium">
                            {usuario.nombre?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{usuario.nombre}</div>
                          <div className="text-xs text-gray-500">
                            {usuario.last_login 
                              ? `Último acceso: ${new Date(usuario.last_login).toLocaleDateString()}` 
                              : 'Nunca ha accedido'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{usuario.email}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        usuario.rol === 'admin' 
                          ? 'bg-purple-100 text-purple-700' 
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {usuario.rol === 'admin' ? 'Administrador' : 'Empresa'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {usuario.empresa_nombre || '-'}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => toggleActivo(usuario)}
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          usuario.activo 
                            ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {usuario.activo ? 'Activo' : 'Inactivo'}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => enviarResetPassword(usuario)}
                          className="p-2 text-gray-600 hover:text-orange-600 hover:bg-orange-50 rounded-lg"
                          title="Enviar reset password"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                        {!usuario.last_login && (
                          <button
                            onClick={() => reenviarInvitacion(usuario)}
                            className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg"
                            title="Reenviar invitación"
                          >
                            <Send className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => abrirModal(usuario)}
                          className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => eliminar(usuario)}
                          className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">{editando ? 'Editar Usuario' : 'Nuevo Usuario'}</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={guardar} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                <input
                  type="text"
                  value={formData.nombre}
                  onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                  disabled={!!editando}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rol *</label>
                <select
                  value={formData.rol}
                  onChange={(e) => setFormData({...formData, rol: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="empresa">Usuario Empresa</option>
                  <option value="admin">Administrador</option>
                </select>
              </div>

              {formData.rol === 'empresa' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Empresa *</label>
                  <select
                    value={formData.empresa_id}
                    onChange={(e) => setFormData({...formData, empresa_id: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Seleccionar empresa...</option>
                    {empresas.map((emp) => (
                      <option key={emp.id} value={emp.id}>{emp.nombre}</option>
                    ))}
                  </select>
                </div>
              )}

              {!editando && (
                <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                  Se enviará un email de invitación al usuario para que establezca su contraseña.
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editando ? 'Guardar' : 'Crear e Invitar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminUsuarios;
